import React, { forwardRef, useImperativeHandle, useState } from 'react'
import {
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  TouchableOpacity,
  TouchableWithoutFeedback
} from 'react-native'

import { DS } from '../../design-system'
import { DefaultSheetContent } from './DefaultSheetContent'
import { DeleteResponse } from './DeleteResponse'
import { styles } from './styles'
import { isWeb, useMaxWidth } from '../WebWrapper'

export interface DailyInspirationCommentRef {
  openModal: () => void
  closeModal: () => void
}

export enum InspirationSheetType {
  DEFAULT = 'DEFAULT',
  EDIT = 'EDIT'
}

type DailyInspirationCommentProps = {
  inspirationMessage: string
  userInitial: string
  onClose: () => void
  onSubmit: (response: string) => void
  type: InspirationSheetType
  editResponseDataId?: string
  editResponseDataMsg?: string
  isReponseLoading?: boolean
  setIsReponseLoading?: (isReponseLoading: boolean) => void
  saveEditResponse?: (responseMsg: string, responseId: string) => void
  onDelete?: (responseId: string) => void
}

export const DailyInspirationCommentSheet = forwardRef((props: DailyInspirationCommentProps, ref) => {
  const maxWidth = useMaxWidth()
  const {
    inspirationMessage,
    userInitial,
    onClose,
    onSubmit,
    type,
    editResponseDataId,
    editResponseDataMsg,
    isReponseLoading,
    setIsReponseLoading,
    saveEditResponse,
    onDelete
  } = props

  const [isCommentSheetModalVisible, setIsCommentSheetModalVisible] = useState(false)
  const [isResponseSubmitted, setIsResponseSubmitted] = useState(false)
  const [bottomSheetHeight, setBottomSheetHeight] = useState('64')
  const [isLoading, setIsLoading] = useState(false)
  const [userResponses, setUserResponses] = useState('')
  const [isDeletingResponse, setIsDeletingResponse] = useState(false)

  const resetCommentSheetParameters = () => {
    setUserResponses('')
    setIsDeletingResponse(false)
  }

  // Exposing methods to parent using ref
  useImperativeHandle(ref, () => ({
    openModal: () => {
      setIsCommentSheetModalVisible(true)
    },
    closeModal: () => {
      resetCommentSheetParameters()
      setIsCommentSheetModalVisible(false)
    }
  }))

  React.useEffect(() => {
    setBottomSheetHeight('64')
    return () => {
      resetCommentSheetParameters()
    }
  }, [])

  const handleEditResponse = () => {
    if (editResponseDataId && saveEditResponse) saveEditResponse(userResponses, editResponseDataId)
  }

  return (
    <Modal transparent visible={isCommentSheetModalVisible}>
      <TouchableOpacity
        style={[
          styles.backdropContainer,
          {
            marginHorizontal: isWeb ? 'auto' : undefined,
            maxWidth: maxWidth,
            width: '100%'
          }
        ]}
        onPress={(e) => {
          e.stopPropagation()
          Keyboard.dismiss()
          setTimeout(() => {
            setIsCommentSheetModalVisible(false)
            setIsDeletingResponse(false)
          }, 0)
        }}
      >
        <TouchableWithoutFeedback>
          {isDeletingResponse ? (
            <DeleteResponse
              onCancel={() => setIsDeletingResponse(false)}
              onDelete={() => {
                setUserResponses('')
                onClose()
                setIsDeletingResponse(false)
                if (editResponseDataId && onDelete) onDelete(editResponseDataId)
              }}
            />
          ) : (
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
              keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
              style={{ width: '100%' }}
            >
              <DS.Frame bg="Brand/Cream" style={styles.defaultSheetContentContainer}>
                {(type === InspirationSheetType.DEFAULT || type === InspirationSheetType.EDIT) && (
                  <DefaultSheetContent
                    isLoading={isLoading}
                    inspirationMessage={inspirationMessage}
                    userInitial={userInitial}
                    userResponses={userResponses}
                    setUserResponses={setUserResponses}
                    setIsLoading={setIsLoading}
                    onClose={() => {
                      setUserResponses('')
                      onClose()
                    }}
                    onSubmit={onSubmit}
                    saveEditResponse={handleEditResponse}
                    setIsModalVisible={setIsCommentSheetModalVisible}
                    setIsResponseSubmitted={setIsResponseSubmitted}
                    editResponseDataMsg={editResponseDataMsg}
                    editResponseDataId={editResponseDataId}
                    isReponseLoading={isReponseLoading}
                    setIsReponseLoading={setIsReponseLoading}
                    type={type}
                    setIsDeletingResponse={setIsDeletingResponse}
                  />
                )}
              </DS.Frame>
            </KeyboardAvoidingView>
          )}
        </TouchableWithoutFeedback>
      </TouchableOpacity>
    </Modal>
  )
})
